import React, { useState, useEffect, useRef } from 'react';

import BrandsCategories from '../Components/BrandsCategories';
import BrandsLegend from '../Components/BrandsLegend';
import { BrandsList } from '../Components/BrandsList';
import BrandsSearch from '../Components/BrandsSearch';
import { SearchList } from '../Components/SearchList';
import { useBrands } from '../hooks/useBrands';

export const BrandsDisplay = () => {
    const [selectedCategoryDimension, setSelectedCategoryDimension] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchFinalized, setIsSearchFinalized] = useState(false);
    const { data: brandsData } = useBrands(selectedCategoryDimension);

    useEffect(() => {
        if (selectedCategoryDimension) {
            setSearchQuery('');
            setIsSearchFinalized(false);
        }
    }, [selectedCategoryDimension]);

    const normalizeString = (str) =>
        str
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

    const searchBrands =
        brandsData?.filter(
            (brand) => !searchQuery || normalizeString(brand.Name).includes(normalizeString(searchQuery)),
        ) || [];

    const filteredBrands = brandsData || [];

    const handleCategorySelect = (dimensionId) => {
        setSelectedCategoryDimension(dimensionId);
    };

    const handleSearchChange = (query, finalizeSearch) => {
        setSearchQuery(query);
        setIsSearchFinalized(finalizeSearch);
    };

    const searchInputRef = useRef<HTMLInputElement>(null);

    const focusOnInput = () => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    return (
        <>
            <div className="mb-16 flex flex-col">
                <h2 className="mb-10 text-left text-2xl font-bold md:text-4xl">Shop by brand</h2>
                <div className="mb-20">
                    <BrandsSearch
                        brandsData={searchBrands}
                        onSearchChange={handleSearchChange}
                        searchInputRef={searchInputRef}
                    />
                    {isSearchFinalized && searchQuery && searchBrands.length > 0 ? (
                        <>
                            <h3 className="mt-16 text-lg font-bold md:text-2xl">
                                Showing brand results for &quot;{searchQuery}&quot;
                            </h3>
                            <SearchList brandsData={searchBrands} category={selectedCategoryDimension} />
                        </>
                    ) : isSearchFinalized && searchQuery && searchBrands.length === 0 ? (
                        <div className="mt-8 flex flex-col bg-slate-50 p-8 text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="mx-auto mb-2 w-10">
                                <path fill="none" d="M0 0h24v24H0z" />
                                <path
                                    fill="#000000"
                                    d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                                />
                            </svg>
                            <p className="text-xl font-bold">
                                We weren&apos;t able to find any brand results for &quot;{searchQuery}&quot;
                            </p>
                            <p>
                                Please check the spelling or{' '}
                                <button onClick={focusOnInput} className="underline">
                                    try another search
                                </button>
                            </p>
                        </div>
                    ) : null}
                </div>
                <div className="flex w-full items-center justify-between pb-12">
                    <div className="flex-1">
                        <BrandsCategories onSelect={handleCategorySelect} />
                    </div>
                </div>
                <BrandsLegend brandsData={filteredBrands} />
                <BrandsList brandsData={brandsData ? brandsData : []} category={selectedCategoryDimension} />
            </div>
        </>
    );
};

export default BrandsDisplay;
